<!-- ===================== PAGE HEADER (TEMPLATE MODE, sticky) =====================
     Rendered OUTSIDE the padded content area by <mvs-page-header> (pulled via getPageHeaderConfig()).
     TWO stacked rows, both inside this one sticky #headerTemplate:
       ROW 1 — the real framework title bar (<mvs-config-header>), restoring the styled chrome / icon /
               category colour that the bare hand-rolled title was missing. NO `links` are passed, so the
               former cross-link buttons (Abgleich / Konfiguration / Dashboard) stay removed — the left-nav
               owns section switching.
       ROW 2 — the WHOLE global Zeitraum + Gesellschaft filter bar, so the filter is a recurring/global
               element that stays visible (sticky) across ALL left-nav sections and drives the data in
               every one (onFilterChange() → refreshDashboard()).
     <mvs-config-header> (selector of MvsConfigHeaderComponent) is declared/exported by SharedModule,
     which CoreModule re-exports and CnModule imports — so it resolves in the cn compilation scope.

     CRITICAL: this <ng-template #headerTemplate> MUST stay at the TOP LEVEL of the file (outside any
     @if), so the static @ViewChild('headerTemplate') on PageComponent resolves before super.ngOnInit()
     triggers the header pull. -->
<ng-template #headerTemplate>

    <!-- ROW 1: framework title bar (icon + "Provisions-Dashboard" + chrome). The three cross-links
         (Import / Eingangsfreigabe / Abgleich) sit in the title row next to the heading via [links],
         rendered as small outlined p-buttons (label + icon) that open the standalone cn routes (hash
         routing, MvsConfigHeaderComponent.openLink()) — mirroring how the standalone cn pages render
         their cross-links. categoryTypeMode="configuration" matches the dashboard's category styling. -->
    <mvs-config-header
        header="Provisions-Dashboard"
        icon="fa-light fa-gauge"
        categoryTypeMode="configuration"
        [links]="headerLinks">
    </mvs-config-header>

    <!-- ROW 2: the GLOBAL Zeitraum + Gesellschaft filter bar — sits directly BENEATH the coloured title
         bar on its own readable surface (surface-0 + padding + bottom border) so its labels/controls are
         legible. Laid out horizontally and wrapping gracefully on narrow widths; labels are kept compact
         (inline, small) to fit the bar. -->
    <div class="cn-dashboard-header surface-0 border-bottom-1 surface-border px-4 py-2 flex align-items-end flex-wrap gap-3">
            <div class="flex flex-column gap-1">
                <label class="text-xs text-gray-600">Zeitraum</label>
                <!-- Zeitraum (range) picker with quick presets (Dieser Monat / Letzter Monat / Dieses Jahr /
                     Letztes Jahr / Benutzerdefiniert). Default preset = Dieser Monat, so the dashboard loads
                     scoped to the current month. The overlay emits {startDate,endDate} (localDate strings);
                     the page derives fromPeriod/toPeriod (YYYY-MM) and refreshes. The preset/range label
                     feeds the "Stand"/Filter-Beschreibung. -->
                <div class="w-16rem">
                    <mvs-range-selector-overlay
                        fieldName="bookingPeriod"
                        placeholder="Alle Perioden"
                        dateMode="localDate"
                        [defaulted]="true"
                        [defaultFilterDate]="defaultFilterDate"
                        (onChangeFilterValues)="onPeriodRangeChange($event)"
                        (filterDateChange)="onPeriodLabelChange($event)">
                    </mvs-range-selector-overlay>
                </div>
            </div>

            <div class="flex flex-column gap-1">
                <label class="text-xs text-gray-600" for="cn-dash-partner">Gesellschaft</label>
                <!-- Gesellschaft DROPDOWN restricted to the CONFIGURED commission partners only
                     (cn.CommissionPartner → partner.name, loaded once via QL) — a small set, so a plain
                     p-select is used. The leading "Alle Gesellschaften" (null) entry is the default.
                     optionValue="id" binds the partner id directly into filterPartnerId via onPartnerChange. -->
                <p-select id="cn-dash-partner"
                          [ngModel]="filterPartnerId"
                          [options]="partnerOptions"
                          optionLabel="name"
                          optionValue="id"
                          [loading]="partnerOptionsLoading"
                          placeholder="Alle Gesellschaften"
                          emptyMessage="Keine Gesellschaften konfiguriert"
                          [filter]="true"
                          filterBy="name"
                          (onChange)="onPartnerChange($event.value)"
                          styleClass="w-16rem">
                </p-select>
            </div>

            @if (hasActiveFilter()) {
                <div class="flex align-items-end pb-1">
                    <p-button label="Filter zurücksetzen"
                              icon="fa-regular fa-filter-circle-xmark"
                              severity="secondary"
                              [text]="true"
                              size="small"
                              (onClick)="resetFilter()">
                    </p-button>
                </div>
            }

            <!-- M11: Aktualität — "Stand:" indicator + manual refresh -->
            <div class="flex align-items-center flex-wrap gap-2 pb-1">
                <!-- Active Zeitraum (preset label or MM.YYYY–MM.YYYY range) so the chosen scope is visible. -->
                <span class="text-xs text-gray-600 white-space-nowrap"
                      pTooltip="Aktiver Zeitraum der angezeigten Kennzahlen."
                      tooltipPosition="bottom">
                    <i class="fa-regular fa-calendar-range mr-1"></i>Zeitraum: {{ activePeriodDescription() }}
                </span>
                @if (lastUpdated) {
                    <span class="text-xs text-gray-600 white-space-nowrap"
                          pTooltip="Zeitpunkt der letzten Aktualisierung dieser Ansicht."
                          tooltipPosition="bottom">
                        <i class="fa-regular fa-clock mr-1"></i>Stand: {{ lastUpdated | date:'dd.MM.yyyy HH:mm' }}
                    </span>
                }
                <p-button label="Aktualisieren"
                          icon="fa-regular fa-arrows-rotate"
                          severity="secondary"
                          [outlined]="true"
                          size="small"
                          pTooltip="Alle Kennzahlen neu vom Server laden."
                          tooltipPosition="bottom"
                          (onClick)="refreshDashboard()">
                </p-button>
            </div>
    </div>

    @if (partnerOptionsError) {
        <p-message severity="warn" class="block px-4 pb-2" [text]="partnerOptionsError"></p-message>
    }
</ng-template>

@if (initialized) {
    <div class="cn-dashboard-container surface-0">

        <!--
            Provisions-Dashboard body (P3 / WP-FE-1): a controlling cockpit organised as a LEFT-NAV
            workflow. The left sidebar (rendered by the app shell from isNavComponent()) switches the
            workflow sections via `?navItem=`; the GLOBAL Periode + Gesellschaft filter bar now lives
            in the (sticky) PAGE HEADER above and drives the data in every section (onFilterChange() →
            refreshDashboard()). Each section opens with an expandable "Was zeigt diese Seite?" panel.
            The former Import / Eingangsfreigabe / Abgleich sections are no longer embedded here — they
            are reachable via the cross-links in the title bar above (their own standalone routes).

            Sections (activeNavItem):
              overview        Controlling-Kennzahlen (KPI-Leiste) + Provisions-Trend
              calculated      Berechnete Provision (Soll) + Soll je Gesellschaft
              received        Provisionssummen (Ist · Quittierung) + Provisionen je Gesellschaft
              inbox           Provisions-Eingangskorb + Quittierungsprotokoll
              dataQuality     Datenqualität-Panel
              sollIstTrend    Soll vs Ist (Verlauf) — Monats-Liniendiagramm
            NO calculation in the FE — all amounts / quotas / counters / the Gesamt come from the backend.
        -->

        <!-- Scrollable section body (the global filter bar lives in the sticky page header above). -->
        <div class="cn-dashboard-sections flex-1 overflow-y-auto">

            <!-- ============================================================================= -->
            <!-- 1) ÜBERSICHT — Controlling-Kennzahlen + Provisions-Trend                      -->
            <!-- ============================================================================= -->
            @if (activeNavItem === 'overview') {

                <!-- Was zeigt diese Seite? -->
                <p-accordion class="block mb-3">
                    <p-accordion-panel value="0">
                        <p-accordion-header>
                            <span class="flex align-items-center gap-2">
                                <i class="fa-regular fa-circle-question text-primary"></i>
                                <span class="font-medium">Was zeigt diese Seite?</span>
                            </span>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-700 p-2">
                                Kennzahlen zur Quittierung (Quote, offen, überfällig) und der
                                Provisions-Trend der letzten Monate. Alle Werte folgen dem oben
                                gewählten Periode- und Gesellschaft-Filter.
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>

                <!-- ===================== CONTROLLING KPI-LEISTE ===================== -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-gauge-high text-gray-600"></i>
                        <span class="text-lg font-medium">Controlling-Kennzahlen</span>
                        <p-button icon="fa-regular fa-file-arrow-down"
                                  label="CSV"
                                  severity="secondary"
                                  [text]="true"
                                  size="small"
                                  styleClass="ml-auto"
                                  pTooltip="Controlling-Kennzahlen als CSV-Datei exportieren."
                                  tooltipPosition="top"
                                  [disabled]="controllingState !== 'ready' || !controlling"
                                  (onClick)="exportControllingCsv()">
                        </p-button>
                    </div>

                    @switch (controllingState) {
                        @case ('loading') {
                            <div class="grid">
                                @for (s of [1,2,3,4,5,6]; track s) {
                                    <div class="col-6 md:col-2">
                                        <p-skeleton height="5rem" styleClass="border-round-md"></p-skeleton>
                                    </div>
                                }
                            </div>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="controllingError"></p-message>
                        }
                        @case ('ready') {
                            @if (controlling) {
                                <div class="grid">
                                    <!-- FE-3: renamed Vollständigkeits-Quote → Quittierungs-Quote; dash (not 0 %) when empty (M10). -->
                                    <div class="col-6 md:col-2">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center">
                                            <div class="text-xs text-gray-600 mb-1 flex align-items-center justify-content-center gap-1">
                                                <span>Quittierungs-Quote</span>
                                                <i class="fa-regular fa-circle-info text-gray-500"
                                                   [pTooltip]="kpiTooltips.completionRate" tooltipPosition="top"></i>
                                            </div>
                                            <div class="text-2xl font-bold text-blue-600">
                                                @if (controlling.completionRatePercent !== null) {
                                                    {{ controlling.completionRatePercent | number:'1.0-1':'de' }}&nbsp;%
                                                } @else {
                                                    –
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-6 md:col-2">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center">
                                            <div class="text-xs text-gray-600 mb-1 flex align-items-center justify-content-center gap-1">
                                                <span>Offen</span>
                                                <i class="fa-regular fa-circle-info text-gray-500"
                                                   [pTooltip]="kpiTooltips.open" tooltipPosition="top"></i>
                                            </div>
                                            <div class="text-2xl font-bold text-orange-600">
                                                {{ controlling.openCount | number:'1.0-0':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-6 md:col-2">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center">
                                            <div class="text-xs text-gray-600 mb-1 flex align-items-center justify-content-center gap-1">
                                                <span>Quittiert</span>
                                                <i class="fa-regular fa-circle-info text-gray-500"
                                                   [pTooltip]="kpiTooltips.acknowledged" tooltipPosition="top"></i>
                                            </div>
                                            <div class="text-2xl font-bold text-green-600">
                                                {{ controlling.acknowledgedCount | number:'1.0-0':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-6 md:col-2">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center">
                                            <div class="text-xs text-gray-600 mb-1 flex align-items-center justify-content-center gap-1">
                                                <span>Überfällig</span>
                                                <i class="fa-regular fa-circle-info text-gray-500"
                                                   [pTooltip]="kpiTooltips.overdue" tooltipPosition="top"></i>
                                            </div>
                                            <div class="text-2xl font-bold"
                                                 [class.text-red-600]="controlling.overdueCount > 0"
                                                 [class.text-gray-600]="controlling.overdueCount === 0">
                                                {{ controlling.overdueCount | number:'1.0-0':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-6 md:col-2">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center">
                                            <div class="text-xs text-gray-600 mb-1 flex align-items-center justify-content-center gap-1">
                                                <span>Heute quittiert</span>
                                                <i class="fa-regular fa-circle-info text-gray-500"
                                                   [pTooltip]="kpiTooltips.throughputToday" tooltipPosition="top"></i>
                                            </div>
                                            <div class="text-2xl font-bold text-blue-600">
                                                {{ controlling.throughputToday | number:'1.0-0':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-6 md:col-2">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center">
                                            <div class="text-xs text-gray-600 mb-1 flex align-items-center justify-content-center gap-1">
                                                <span>Ältester offener Vorgang</span>
                                                <i class="fa-regular fa-circle-info text-gray-500"
                                                   [pTooltip]="kpiTooltips.oldestOpen" tooltipPosition="top"></i>
                                            </div>
                                            <div class="text-xl font-bold">
                                                {{ controlling.oldestOpenPeriod ?? '–' }}
                                            </div>
                                            @if (controlling.oldestOpenAgeDays != null) {
                                                <div class="text-xs text-gray-600 mt-1">
                                                    seit {{ controlling.oldestOpenAgeDays | number:'1.0-0':'de' }} Tagen offen
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            } @else {
                                <div class="text-center text-gray-600 p-4">
                                    Keine Daten für den gewählten Zeitraum.
                                </div>
                            }
                        }
                    }
                </div>

                <!-- ===================== TREND: AP/BP + vs. VORMONAT (FE-5) ===================== -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-chart-line text-gray-600"></i>
                        <span class="text-lg font-medium">Provisions-Trend</span>
                        <!-- Jean-Marie #7: honest range — "(Daten ab YYYY-MM)" when only the tail of the window is
                             populated, otherwise the plain "(letzte N Monate)" wording. -->
                        <span class="text-sm text-gray-600">({{ trendRangeSubtitle() }})</span>
                        <p-button icon="fa-regular fa-file-arrow-down"
                                  label="CSV"
                                  severity="secondary"
                                  [text]="true"
                                  size="small"
                                  styleClass="ml-auto"
                                  pTooltip="Provisions-Trend als CSV-Datei exportieren (mit vs.-Vormonat-Delta)."
                                  tooltipPosition="top"
                                  [disabled]="trendState !== 'ready' || !trend"
                                  (onClick)="exportTrendCsv()">
                        </p-button>
                    </div>

                    @switch (trendState) {
                        @case ('loading') {
                            <p-skeleton height="8rem" styleClass="border-round-md"></p-skeleton>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="trendError"></p-message>
                        }
                        @case ('empty') {
                            <div class="text-center text-gray-600 p-4">
                                Keine Daten für den gewählten Zeitraum.
                            </div>
                        }
                        @case ('ready') {
                            @if (trend) {
                                <!-- vs. Vormonat delta tiles (EUR + %; null % → Strich) -->
                                <div class="grid mb-3">
                                    <div class="col-12 md:col-4">
                                        <div class="surface-50 border-round-md p-3 h-full">
                                            <div class="text-xs text-gray-600 mb-1">AP · vs. Vormonat</div>
                                            <div class="text-xl font-bold"
                                                 [class.text-green-600]="trend.delta.apDelta > 0"
                                                 [class.text-red-600]="trend.delta.apDelta < 0">
                                                {{ trend.delta.apDelta | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                @if (trend.delta.apDeltaPercent !== null) {
                                                    {{ trend.delta.apDeltaPercent | number:'1.0-1':'de' }}&nbsp;%
                                                } @else {
                                                    –
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-4">
                                        <div class="surface-50 border-round-md p-3 h-full">
                                            <div class="text-xs text-gray-600 mb-1">BP · vs. Vormonat</div>
                                            <div class="text-xl font-bold"
                                                 [class.text-green-600]="trend.delta.bpDelta > 0"
                                                 [class.text-red-600]="trend.delta.bpDelta < 0">
                                                {{ trend.delta.bpDelta | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                @if (trend.delta.bpDeltaPercent !== null) {
                                                    {{ trend.delta.bpDeltaPercent | number:'1.0-1':'de' }}&nbsp;%
                                                } @else {
                                                    –
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-4">
                                        <div class="surface-50 border-round-md p-3 h-full">
                                            <div class="text-xs text-gray-600 mb-1">Gesamt · vs. Vormonat</div>
                                            <div class="text-xl font-bold"
                                                 [class.text-green-600]="trend.delta.gesamtDelta > 0"
                                                 [class.text-red-600]="trend.delta.gesamtDelta < 0">
                                                {{ trend.delta.gesamtDelta | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                            <div class="text-sm text-gray-600">
                                                @if (trend.delta.gesamtDeltaPercent !== null) {
                                                    {{ trend.delta.gesamtDeltaPercent | number:'1.0-1':'de' }}&nbsp;%
                                                } @else {
                                                    –
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Compact per-period AP/BP/Gesamt table — uses the honest (leading-zero-trimmed)
                                     display series so the axis does not imply unpopulated months (Jean-Marie #7). -->
                                <p-table [value]="trendDisplaySeries()" styleClass="p-datatable-sm">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th class="w-8rem">Periode</th>
                                            <th class="text-right">AP</th>
                                            <th class="text-right">BP</th>
                                            <th class="text-right">Gesamt</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-point>
                                        <tr>
                                            <td>{{ point.period }}</td>
                                            <td class="text-right">{{ point.ap | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                            <td class="text-right">{{ point.bp | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                            <td class="text-right font-medium">{{ point.gesamt | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            }
                        }
                    }
                </div>
            }

            <!-- ============================================================================= -->
            <!-- 2) BERECHNETE PROVISION (SOLL) — calculated expectation + Soll je Gesellschaft -->
            <!-- ============================================================================= -->
            @if (activeNavItem === 'calculated') {

                <!-- Was zeigt diese Seite? -->
                <p-accordion class="block mb-3">
                    <p-accordion-panel value="0">
                        <p-accordion-header>
                            <span class="flex align-items-center gap-2">
                                <i class="fa-regular fa-circle-question text-primary"></i>
                                <span class="font-medium">Was zeigt diese Seite?</span>
                            </span>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-700 p-2">
                                Die von uns BERECHNETE erwartete Provision (Soll) je Provisionsart —
                                Grundlage: hinterlegte Provisionsmodelle × Verträge. Noch kein
                                Abgleich mit der Gesellschaft.
                                <span class="block mt-2 text-sm text-600">
                                    Tipp: Klicken Sie auf einen AP-, BP- oder Sonstige-Betrag, um die
                                    zugrunde liegenden Verträge rechts aufzuklappen.
                                </span>
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>

                <!-- ===================== OVERVIEW → DETAIL (Fiori FlexibleColumnLayout) =====================
                     A p-splitter hosts the Soll overview on the LEFT and the contract drill-down table on
                     the RIGHT. Default: detail collapsed → overview at full width. On a drill-down click the
                     overview shrinks to ~40 % and the detail table takes ~60 % (drillDownPanelSizes). A ×
                     button on the detail panel collapses it back to a full-width overview. -->
                <p-splitter layout="horizontal"
                            styleClass="border-none"
                            [panelSizes]="drillDownPanelSizes"
                            [minSizes]="drillDownOpen ? [30, 35] : [100, 0]">

                    <!-- LEFT: Soll overview (shrinks when a drill-down is open) -->
                    <ng-template pTemplate>
                        <div class="w-full pr-2">

                <!-- ===================== BERECHNETE PROVISION (SOLL) =====================
                     The CALCULATED commission expectation (Soll) per provision type, from
                     GET /cn/calculation/totals. Single total per bucket (kein Offen/Quittiert-Split).
                     NO calculation in the FE — the four bucket totals come from the backend. -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-2">
                        <i class="fa-regular fa-calculator text-gray-600"></i>
                        <span class="text-lg font-medium">Berechnete Provision (Soll)</span>
                        <i class="fa-regular fa-circle-info text-gray-500"
                           pTooltip="Soll = die berechnete Provisionserwartung aus den Provisionsmodellen (nicht die quittierte Ist-Summe). Gleicher Periode- und Gesellschaft-Filter wie das übrige Dashboard."
                           tooltipPosition="top"></i>
                        <!-- Soll-Neuberechnung: triggers POST /cn/commission-recalc/{partnerId}/recalculate
                             for the SELECTED Gesellschaft only. Disabled (with a hint tooltip) when no
                             Gesellschaft is chosen — a recalc-all is heavy and must not fire by accident. -->
                        <p-button label="Neu berechnen"
                                  icon="fa-solid fa-rotate"
                                  severity="primary"
                                  [outlined]="true"
                                  size="small"
                                  styleClass="ml-auto"
                                  [loading]="recalculating"
                                  [disabled]="!canRecalculate()"
                                  [pTooltip]="filterPartnerId != null
                                      ? 'Berechnet die Provisionen der gewählten Gesellschaft über alle Verträge neu (idempotent: legt an / aktualisiert / entfernt).'
                                      : 'Bitte zuerst eine Gesellschaft wählen'"
                                  tooltipPosition="top"
                                  (onClick)="recalculate()">
                        </p-button>
                    </div>

                    <!-- Optional explainer next to the action (kept short; the full wording is in the tooltip). -->
                    <div class="text-sm text-gray-600 mb-3">
                        Berechnet die Provisionen der gewählten Gesellschaft über alle Verträge neu
                        (idempotent: legt an / aktualisiert / entfernt).
                    </div>

                    <!-- Result / error feedback of the last Neuberechnung (state-driven inline message,
                         consistent with the rest of the dashboard). -->
                    @if (recalcMessage) {
                        <p-message [severity]="recalcMessageSeverity" class="block mb-3" [text]="recalcMessage"></p-message>
                    }

                    @switch (calculatedTotalsState) {
                        @case ('loading') {
                            <div class="grid">
                                @for (s of [1,2,3,4]; track s) {
                                    <div class="col-12 md:col-3">
                                        <p-skeleton height="6rem" styleClass="border-round-md"></p-skeleton>
                                    </div>
                                }
                            </div>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="calculatedTotalsError"></p-message>
                        }
                        @case ('ready') {
                            @if (calculatedTotals) {
                                <!-- The AP / BP / Sonstige / Gesamt tiles are CLICKABLE drill-down targets only when a
                                     single Gesellschaft is selected (the tiles aggregate ALL Gesellschaften otherwise,
                                     so there is no single partnerId to scope the contract list to). The Gesamt tile
                                     drills into ALL provision types (AP, BP & Sonstige together) for the selected
                                     Gesellschaft + Zeitraum. -->
                                <div class="grid">
                                    <div class="col-12 md:col-3">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center"
                                             [class.cursor-pointer]="filterPartnerId != null"
                                             [class.hover:surface-100]="filterPartnerId != null"
                                             [pTooltip]="filterPartnerId != null ? 'Verträge dieser Gesellschaft (AP) anzeigen' : 'Bitte zuerst eine Gesellschaft wählen, um die Verträge aufzuklappen'"
                                             tooltipPosition="top"
                                             (click)="filterPartnerId != null && openCalculatedDrillDown(filterPartnerId, selectedPartner?.name ?? null, 'AP')">
                                            <div class="text-xs text-gray-600 mb-1">
                                                Abschlussprovision (AP)
                                                @if (filterPartnerId != null) {
                                                    <i class="fa-regular fa-arrow-up-right-from-square text-primary ml-1"></i>
                                                }
                                            </div>
                                            <div class="text-2xl font-bold text-blue-600">
                                                {{ calculatedTotals.ap.total | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-3">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center"
                                             [class.cursor-pointer]="filterPartnerId != null"
                                             [class.hover:surface-100]="filterPartnerId != null"
                                             [pTooltip]="filterPartnerId != null ? 'Verträge dieser Gesellschaft (BP) anzeigen' : 'Bitte zuerst eine Gesellschaft wählen, um die Verträge aufzuklappen'"
                                             tooltipPosition="top"
                                             (click)="filterPartnerId != null && openCalculatedDrillDown(filterPartnerId, selectedPartner?.name ?? null, 'BP')">
                                            <div class="text-xs text-gray-600 mb-1">
                                                Bestandspflegeprovision (BP)
                                                @if (filterPartnerId != null) {
                                                    <i class="fa-regular fa-arrow-up-right-from-square text-primary ml-1"></i>
                                                }
                                            </div>
                                            <div class="text-2xl font-bold text-blue-600">
                                                {{ calculatedTotals.bp.total | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-3">
                                        <div class="surface-50 border-round-md p-3 text-center h-full flex flex-column justify-content-center"
                                             [class.cursor-pointer]="filterPartnerId != null"
                                             [class.hover:surface-100]="filterPartnerId != null"
                                             [pTooltip]="filterPartnerId != null ? 'Verträge dieser Gesellschaft (Sonstige) anzeigen' : 'Bitte zuerst eine Gesellschaft wählen, um die Verträge aufzuklappen'"
                                             tooltipPosition="top"
                                             (click)="filterPartnerId != null && openCalculatedDrillDown(filterPartnerId, selectedPartner?.name ?? null, 'SONSTIGE')">
                                            <div class="text-xs text-gray-600 mb-1">
                                                Sonstige
                                                @if (filterPartnerId != null) {
                                                    <i class="fa-regular fa-arrow-up-right-from-square text-primary ml-1"></i>
                                                }
                                            </div>
                                            <div class="text-2xl font-bold text-gray-700">
                                                {{ calculatedTotals.sonstige.total | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-3">
                                        <div class="surface-100 border-round-md p-3 text-center h-full flex flex-column justify-content-center border-left-3 border-primary"
                                             [class.cursor-pointer]="filterPartnerId != null"
                                             [class.hover:surface-200]="filterPartnerId != null"
                                             [pTooltip]="filterPartnerId != null ? 'Alle Verträge dieser Gesellschaft (alle Provisionsarten) anzeigen' : 'Bitte zuerst eine Gesellschaft wählen, um die Verträge aufzuklappen'"
                                             tooltipPosition="top"
                                             (click)="filterPartnerId != null && openCalculatedDrillDown(filterPartnerId, selectedPartner?.name ?? null, 'TOTAL')">
                                            <div class="text-xs text-gray-600 mb-1">
                                                Gesamt
                                                @if (filterPartnerId != null) {
                                                    <i class="fa-regular fa-arrow-up-right-from-square text-primary ml-1"></i>
                                                }
                                            </div>
                                            <div class="text-3xl font-bold text-primary" [class.underline]="filterPartnerId != null">
                                                {{ calculatedTotals.gesamt.total | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            } @else {
                                <div class="text-center text-gray-600 p-4">
                                    Keine berechnete Provision für den gewählten Zeitraum.
                                </div>
                            }
                        }
                    }
                </div>

                <!-- ===================== BERECHNETE PROVISION JE GESELLSCHAFT (SOLL) =====================
                     One row per Gesellschaft (Gesellschaft · AP · BP · AP+BP · Gesamt) from CALCULATED
                     records (GET /cn/calculation/totals/by-partner) plus a Summen row. NO FE calculation
                     on the per-row figures; AP+BP and the Summen row are plain additive aggregations. -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-building text-gray-600"></i>
                        <span class="text-lg font-medium">Berechnete Provision je Gesellschaft (Soll)</span>
                    </div>

                    @switch (calculatedByPartnerState) {
                        @case ('loading') {
                            <p-skeleton height="10rem" styleClass="border-round-md"></p-skeleton>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="calculatedByPartnerError"></p-message>
                        }
                        @case ('empty') {
                            <div class="text-center text-gray-600 p-4">
                                Keine berechnete Provision für den gewählten Zeitraum.
                            </div>
                        }
                        @case ('ready') {
                            <!-- The AP / BP / Sonstige / Gesamt amount cells per Gesellschaft are CLICKABLE: a click
                                 opens the contract drill-down (right panel) scoped to {partnerId, provisionType}.
                                 Sonstige = Gesamt − (AP + BP); its cell drills into all non-AP/BP provision types.
                                 Gesamt drills into ALL provision types (no provisionType scope) for that Gesellschaft. -->
                            <p-table [value]="calculatedByPartner" styleClass="p-datatable-sm" [scrollable]="true">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Gesellschaft</th>
                                        <th class="text-right w-10rem">AP</th>
                                        <th class="text-right w-10rem">BP</th>
                                        <th class="text-right w-10rem">Sonstige</th>
                                        <th class="text-right w-10rem">AP + BP</th>
                                        <th class="text-right w-10rem">Gesamt</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td>{{ row.partnerName ?? '–' }}</td>
                                        <td class="text-right cursor-pointer text-primary hover:underline"
                                            pTooltip="Verträge (AP) anzeigen" tooltipPosition="left"
                                            (click)="openCalculatedDrillDown(row.partnerId, row.partnerName, 'AP')">
                                            {{ row.ap | currency:'EUR':'symbol':'1.2-2':'de' }}
                                        </td>
                                        <td class="text-right cursor-pointer text-primary hover:underline"
                                            pTooltip="Verträge (BP) anzeigen" tooltipPosition="left"
                                            (click)="openCalculatedDrillDown(row.partnerId, row.partnerName, 'BP')">
                                            {{ row.bp | currency:'EUR':'symbol':'1.2-2':'de' }}
                                        </td>
                                        <td class="text-right cursor-pointer text-primary hover:underline"
                                            pTooltip="Verträge (Sonstige) anzeigen" tooltipPosition="left"
                                            (click)="openCalculatedDrillDown(row.partnerId, row.partnerName, 'SONSTIGE')">
                                            {{ (row.gesamt - calcApBp(row)) | currency:'EUR':'symbol':'1.2-2':'de' }}
                                        </td>
                                        <td class="text-right font-semibold text-primary">
                                            {{ calcApBp(row) | currency:'EUR':'symbol':'1.2-2':'de' }}
                                        </td>
                                        <td class="text-right cursor-pointer text-primary hover:underline"
                                            pTooltip="Alle Verträge dieser Gesellschaft (alle Provisionsarten) anzeigen" tooltipPosition="left"
                                            (click)="openCalculatedDrillDown(row.partnerId, row.partnerName, 'TOTAL')">
                                            {{ row.gesamt | currency:'EUR':'symbol':'1.2-2':'de' }}
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="footer">
                                    <tr class="font-bold surface-50">
                                        <td>Summe</td>
                                        <td class="text-right">{{ calculatedByPartnerSum('ap') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right">{{ calculatedByPartnerSum('bp') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right">{{ (calculatedByPartnerSum('gesamt') - calculatedByPartnerApBpSum()) | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right text-primary">{{ calculatedByPartnerApBpSum() | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right">{{ calculatedByPartnerSum('gesamt') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }
                    }
                </div>

                        </div>
                    </ng-template>

                    <!-- RIGHT: contract drill-down table (QL widget on cn.CommissionCalculation).
                         Only rendered once a drill-down is open; a × button collapses it back. -->
                    <ng-template pTemplate>
                        @if (drillDownOpen) {
                            <div class="w-full pl-2">
                                <div class="surface-0 border-1 surface-border border-round-lg p-3 h-full">
                                    <div class="flex align-items-center gap-2 mb-3">
                                        <i class="fa-regular fa-file-contract text-gray-600"></i>
                                        <span class="text-lg font-medium">{{ drillDownTitle }}</span>
                                        <p-button icon="fa-solid fa-xmark"
                                                  severity="secondary"
                                                  [rounded]="true"
                                                  [text]="true"
                                                  size="small"
                                                  styleClass="ml-auto"
                                                  pTooltip="Detailansicht schließen"
                                                  tooltipPosition="left"
                                                  ariaLabel="Detailansicht schließen"
                                                  (onClick)="closeCalculatedDrillDown()">
                                        </p-button>
                                    </div>

                                    <!-- QL table widget on cn.CommissionCalculation with predefined filters
                                         (partnerId + provisionType + bookingMonth range). Columns: Vertrag,
                                         Buchungsperiode, Provisionsart, Bemessungsbasis, Satz, Brutto, Modell,
                                         Status — driven by the entity's widget metadata. -->
                                    @if (drillDownWidgetData) {
                                        <mvs-widget [widgetData]="drillDownWidgetData"></mvs-widget>
                                    }
                                </div>
                            </div>
                        }
                    </ng-template>

                </p-splitter>
            }

            <!-- ============================================================================= -->
            <!-- 3) EMPFANGENE PROVISION (IST) — Provisionssummen-Matrix + je Gesellschaft     -->
            <!-- ============================================================================= -->
            @if (activeNavItem === 'received') {

                <!-- Was zeigt diese Seite? -->
                <p-accordion class="block mb-3">
                    <p-accordion-panel value="0">
                        <p-accordion-header>
                            <span class="flex align-items-center gap-2">
                                <i class="fa-regular fa-circle-question text-primary"></i>
                                <span class="font-medium">Was zeigt diese Seite?</span>
                            </span>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-700 p-2">
                                Die tatsächlich von der Gesellschaft empfangenen/quittierten
                                Provisionen (Ist), aufgeteilt in offen vs. quittiert.
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>

                <!-- ===================== PROVISIONSSUMMEN (AP / BP / SONSTIGE / GESAMT) ===================== -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-coins text-gray-600"></i>
                        <span class="text-lg font-medium">Provisionssummen (Ist · Quittierung)</span>
                        <i class="fa-regular fa-circle-info text-gray-500"
                           pTooltip="Ist = die tatsächlich erfassten / quittierten Provisionen (Quittierung). Die berechnete Erwartung (Soll) steht im Abschnitt »Berechnete Provision (Soll)«."
                           tooltipPosition="top"></i>
                        <p-button icon="fa-regular fa-file-arrow-down"
                                  label="CSV"
                                  severity="secondary"
                                  [text]="true"
                                  size="small"
                                  styleClass="ml-auto"
                                  pTooltip="Provisionssummen als CSV-Datei exportieren."
                                  tooltipPosition="top"
                                  [disabled]="totalsState !== 'ready' || !totals"
                                  (onClick)="exportTotalsCsv()">
                        </p-button>
                    </div>

                    @switch (totalsState) {
                        @case ('loading') {
                            <div class="grid">
                                @for (s of [1,2,3]; track s) {
                                    <div class="col-12 md:col-4">
                                        <p-skeleton height="6rem" styleClass="border-round-md"></p-skeleton>
                                    </div>
                                }
                            </div>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="totalsError"></p-message>
                        }
                        @case ('ready') {
                            @if (totals) {
                                <!-- Jean-Marie BLOCKER/MAJOR: the ATTRIBUTED business AP+BP is the real headline
                                     figure. Shown as a prominent banner ABOVE and stronger than the
                                     Sonstige-dominated Gesamt row, so the eye lands on AP+BP first. -->
                                <div class="surface-100 border-round-lg p-3 mb-3 border-left-3 border-primary">
                                    <div class="flex align-items-center gap-2 mb-2">
                                        <i class="fa-regular fa-bullseye-arrow text-primary"></i>
                                        <span class="text-base font-semibold">Zugeordnetes Provisionsgeschäft (AP + BP)</span>
                                        <i class="fa-regular fa-circle-info text-gray-500"
                                           pTooltip="Das tatsächlich zugeordnete Provisionsgeschäft aus Abschluss- (AP) und Bestandspflegeprovision (BP). Dies ist die maßgebliche Kennzahl — die Zeile Gesamt enthält zusätzlich nicht zuordenbare Sonstige."
                                           tooltipPosition="top"></i>
                                    </div>
                                    <div class="grid">
                                        <div class="col-12 md:col-4">
                                            <div class="text-xs text-gray-600 mb-1">Offen</div>
                                            <div class="text-2xl font-bold"
                                                 [class.text-orange-600]="apBpOpen() >= 0"
                                                 [class.text-red-600]="apBpOpen() < 0">
                                                {{ apBpOpen() | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                        <div class="col-12 md:col-4">
                                            <div class="text-xs text-gray-600 mb-1">Quittiert</div>
                                            <div class="text-2xl font-bold"
                                                 [class.text-green-600]="apBpAcknowledged() >= 0"
                                                 [class.text-red-600]="apBpAcknowledged() < 0">
                                                {{ apBpAcknowledged() | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                        <div class="col-12 md:col-4">
                                            <div class="text-xs text-gray-600 mb-1">Gesamt (AP + BP)</div>
                                            <div class="text-3xl font-bold text-primary"
                                                 [class.text-red-600]="apBpTotal() < 0">
                                                {{ apBpTotal() | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- AP / BP / SONSTIGE / GESAMT — SONSTIGE is a full prominent row (FE-3); the
                                     Gesamt row uses the BACKEND total bucket, never an FE recompute. The Gesamt
                                     row is now de-emphasised vs. the AP+BP banner above (Jean-Marie). -->
                                <p-table [value]="[totals.ap, totals.bp, totals.sonstige, totals.gesamt]" styleClass="p-datatable-sm">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>Provisionsart</th>
                                            <th class="text-right w-12rem">Offen</th>
                                            <th class="text-right w-12rem">Quittiert</th>
                                            <!-- TM-1000001915 (c): this Gesamt column INCLUDES Sonstige, so it differs
                                                 from the AP+BP banner total above (which excludes Sonstige). Label it
                                                 explicitly to avoid the "two Gesamt figures" confusion. -->
                                            <th class="text-right w-12rem">Gesamt (inkl. Sonstige)</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-row let-i="rowIndex">
                                        <tr [class.font-bold]="i === 3" [class.surface-50]="i === 3">
                                            <td>
                                                @switch (i) {
                                                    @case (0) { Abschlussprovision (AP) }
                                                    @case (1) { Bestandspflegeprovision (BP) }
                                                    @case (2) { Sonstige }
                                                    @default { Gesamt }
                                                }
                                            </td>
                                            <td class="text-right"
                                                [class.text-orange-600]="row.open >= 0"
                                                [class.text-red-600]="row.open < 0">
                                                {{ row.open | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </td>
                                            <td class="text-right"
                                                [class.text-green-600]="row.acknowledged >= 0"
                                                [class.text-red-600]="row.acknowledged < 0">
                                                {{ row.acknowledged | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </td>
                                            <td class="text-right"
                                                [class.text-red-600]="(row.open + row.acknowledged) < 0">
                                                @if (i === 3) {
                                                    {{ (totals.gesamt.open + totals.gesamt.acknowledged) | currency:'EUR':'symbol':'1.2-2':'de' }}
                                                } @else {
                                                    {{ (row.open + row.acknowledged) | currency:'EUR':'symbol':'1.2-2':'de' }}
                                                }
                                            </td>
                                        </tr>
                                    </ng-template>
                                </p-table>

                                @if ((totals.sonstige.open + totals.sonstige.acknowledged) !== 0) {
                                    <p-message severity="info" class="block mt-2"
                                               text="Sonstige: konnten keiner Provisionsart (AP/BP) zugeordnet werden.">
                                    </p-message>
                                }
                            } @else {
                                <div class="text-center text-gray-600 p-4">
                                    Keine Daten für den gewählten Zeitraum.
                                </div>
                            }
                        }
                    }
                </div>

                <!-- ===================== PROVISIONEN JE GESELLSCHAFT (CROSS-GESELLSCHAFT) =====================
                     Jean-Marie MAJOR: one row per Gesellschaft (Gesellschaft · AP · BP · AP+BP · Gesamt) plus a
                     Summen row, from GET /cn/activity/totals/by-partner. CSV-exportable. NO FE calculation on the
                     per-row figures; the Summen row is a plain column total of the listed rows. -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-building text-gray-600"></i>
                        <span class="text-lg font-medium">Provisionen je Gesellschaft</span>
                        <p-button icon="fa-regular fa-file-arrow-down"
                                  label="CSV"
                                  severity="secondary"
                                  [text]="true"
                                  size="small"
                                  styleClass="ml-auto"
                                  pTooltip="Provisionen je Gesellschaft als CSV-Datei exportieren."
                                  tooltipPosition="top"
                                  [disabled]="byPartnerState !== 'ready' || !byPartner.length"
                                  (onClick)="exportByPartnerCsv()">
                        </p-button>
                    </div>

                    @switch (byPartnerState) {
                        @case ('loading') {
                            <p-skeleton height="10rem" styleClass="border-round-md"></p-skeleton>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="byPartnerError"></p-message>
                        }
                        @case ('empty') {
                            <div class="text-center text-gray-600 p-4">
                                Keine Daten für den gewählten Zeitraum.
                            </div>
                        }
                        @case ('ready') {
                            <p-table [value]="byPartner" styleClass="p-datatable-sm" [scrollable]="true">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Gesellschaft</th>
                                        <th class="text-right w-12rem">AP</th>
                                        <th class="text-right w-12rem">BP</th>
                                        <th class="text-right w-12rem">AP + BP</th>
                                        <!-- TM-1000001915 (c): Gesamt here likewise INCLUDES Sonstige (the AP+BP column
                                             is the Sonstige-free figure), so it is labelled to match the Provisionsart table. -->
                                        <th class="text-right w-12rem">Gesamt (inkl. Sonstige)</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td>{{ row.partnerName ?? '–' }}</td>
                                        <td class="text-right">{{ row.ap | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right">{{ row.bp | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right font-semibold text-primary">
                                            {{ row.apBp | currency:'EUR':'symbol':'1.2-2':'de' }}
                                        </td>
                                        <td class="text-right">{{ row.gesamt | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="footer">
                                    <tr class="font-bold surface-50">
                                        <td>Summe</td>
                                        <td class="text-right">{{ byPartnerSum('ap') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right">{{ byPartnerSum('bp') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right text-primary">{{ byPartnerSum('apBp') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                        <td class="text-right">{{ byPartnerSum('gesamt') | currency:'EUR':'symbol':'1.2-2':'de' }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }
                    }
                </div>
            }

            <!-- ============================================================================= -->
            <!-- 4) AKTIONSKORB — Provisions-Eingangskorb + Quittierungsprotokoll              -->
            <!-- ============================================================================= -->
            @if (activeNavItem === 'inbox') {

                <!-- Was zeigt diese Seite? -->
                <p-accordion class="block mb-3">
                    <p-accordion-panel value="0">
                        <p-accordion-header>
                            <span class="flex align-items-center gap-2">
                                <i class="fa-regular fa-circle-question text-primary"></i>
                                <span class="font-medium">Was zeigt diese Seite?</span>
                            </span>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-700 p-2">
                                Offene Provisions-Aktivitäten, die der Spezialist prüfen und
                                quittieren muss (Neuzugang, Storno, Abgleich-Findings). Darunter das
                                Quittierungsprotokoll der bereits quittierten Vorgänge.
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>

                <!-- ===================== PROVISIONS-EINGANGSKORB (FE-INBOX owns the component) =====================
                     FE-1: the dashboard passes the global Periode + Gesellschaft into the inbox so it scopes its
                     own list + KPI tiles, and listens to (acknowledged) to refresh the controlling cockpit live.
                     The inbox is the single owner of its own scoped sub-counts; the controlling bar stays
                     the single source of truth for the cockpit KPIs. -->
                <mvs-cn-activity-inbox
                    [period]="resolvePeriod()"
                    [partnerId]="resolvePartnerId()"
                    (acknowledged)="reloadTotalsControllingProtocol()">
                </mvs-cn-activity-inbox>

                <!-- ===================== QUITTIERUNGSPROTOKOLL (AUDIT) ===================== -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3 mt-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-clipboard-check text-gray-600"></i>
                        <span class="text-lg font-medium">Quittierungsprotokoll</span>
                        <p-button icon="fa-regular fa-file-arrow-down"
                                  label="CSV"
                                  severity="secondary"
                                  [text]="true"
                                  size="small"
                                  styleClass="ml-auto"
                                  pTooltip="Quittierungsprotokoll als CSV-Datei exportieren (mit aktivem Filter)."
                                  tooltipPosition="top"
                                  [disabled]="protocolState !== 'ready' || !protocolEntries.length"
                                  (onClick)="exportProtocolCsv()">
                        </p-button>
                    </div>

                    <!-- Hans-Jürgen: audit filters "Quittiert von" (acknowledgedBy) + "Quittiert am von–bis"
                         (acknowledgedFrom / acknowledgedTo). These hit the already-supported GET /cn/activity
                         params and are documented in the protocol CSV header line. -->
                    <div class="flex align-items-end flex-wrap gap-3 mb-3">
                        <div class="flex flex-column gap-1">
                            <label class="text-sm text-gray-600" for="cn-prot-ackby">Quittiert von</label>
                            <input pInputText id="cn-prot-ackby"
                                   [(ngModel)]="protocolAckByFilter"
                                   placeholder="Sachbearbeiter"
                                   (keyup.enter)="onProtocolFilterChange()"
                                   class="w-14rem">
                        </div>
                        <div class="flex flex-column gap-1">
                            <label class="text-sm text-gray-600" for="cn-prot-ackfrom">Quittiert am von</label>
                            <p-datePicker id="cn-prot-ackfrom"
                                          [(ngModel)]="protocolAckFromDate"
                                          dateFormat="dd.mm.yy"
                                          [showIcon]="true"
                                          [showButtonBar]="true"
                                          placeholder="von"
                                          (onSelect)="onProtocolFilterChange()"
                                          (onClear)="onProtocolFilterChange()"
                                          styleClass="w-12rem">
                            </p-datePicker>
                        </div>
                        <div class="flex flex-column gap-1">
                            <label class="text-sm text-gray-600" for="cn-prot-ackto">Quittiert am bis</label>
                            <p-datePicker id="cn-prot-ackto"
                                          [(ngModel)]="protocolAckToDate"
                                          dateFormat="dd.mm.yy"
                                          [showIcon]="true"
                                          [showButtonBar]="true"
                                          placeholder="bis"
                                          (onSelect)="onProtocolFilterChange()"
                                          (onClear)="onProtocolFilterChange()"
                                          styleClass="w-12rem">
                            </p-datePicker>
                        </div>
                        <p-button label="Suchen"
                                  icon="fa-regular fa-magnifying-glass"
                                  severity="secondary"
                                  [outlined]="true"
                                  size="small"
                                  (onClick)="onProtocolFilterChange()">
                        </p-button>
                        @if (hasActiveProtocolFilter()) {
                            <p-button label="Filter zurücksetzen"
                                      icon="fa-regular fa-filter-circle-xmark"
                                      severity="secondary"
                                      [text]="true"
                                      size="small"
                                      (onClick)="resetProtocolFilter()">
                            </p-button>
                        }
                    </div>

                    @switch (protocolState) {
                        @case ('loading') {
                            <p-skeleton height="10rem" styleClass="border-round-md"></p-skeleton>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="protocolError"></p-message>
                        }
                        @case ('empty') {
                            <div class="text-center text-gray-600 p-4">
                                Keine Daten für den gewählten Zeitraum.
                            </div>
                        }
                        @case ('ready') {
                            <!-- Hans-Jürgen #8: the protocol columns are client-side sortable (pSortableColumn +
                                 p-sortIcon). The initial sort stays "Quittiert am" (acknowledgedAt) DESC so the
                                 newest quittierten Vorgänge lead until the user clicks another column. -->
                            <p-table [value]="protocolEntries"
                                     styleClass="p-datatable-sm"
                                     [scrollable]="true"
                                     [sortField]="'acknowledgedAt'"
                                     [sortOrder]="-1">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th pSortableColumn="partnerName">Gesellschaft <p-sortIcon field="partnerName"></p-sortIcon></th>
                                        <th class="w-8rem" pSortableColumn="kind">Art <p-sortIcon field="kind"></p-sortIcon></th>
                                        <th class="text-right w-10rem" pSortableColumn="amount">Betrag <p-sortIcon field="amount"></p-sortIcon></th>
                                        <th class="w-12rem" pSortableColumn="acknowledgedBy">Quittiert von <p-sortIcon field="acknowledgedBy"></p-sortIcon></th>
                                        <th class="w-10rem" pSortableColumn="acknowledgedAt">Quittiert am <p-sortIcon field="acknowledgedAt"></p-sortIcon></th>
                                        <th pSortableColumn="note">Notiz <p-sortIcon field="note"></p-sortIcon></th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-row>
                                    <tr>
                                        <td>{{ row.partnerName || row.partner || '–' }}</td>
                                        <td>{{ activityKindLabel(row.kind) }}</td>
                                        <td class="text-right">
                                            @if (row.amount != null) {
                                                {{ row.amount | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            } @else {
                                                –
                                            }
                                        </td>
                                        <td>{{ row.acknowledgedBy || '–' }}</td>
                                        <td>
                                            @if (row.acknowledgedAt) {
                                                {{ row.acknowledgedAt | date:'dd.MM.yyyy HH:mm' }}
                                            } @else {
                                                –
                                            }
                                        </td>
                                        <td class="text-gray-600">{{ activityNote(row) ?? '–' }}</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        }
                    }
                </div>
            }

            <!-- ============================================================================= -->
            <!-- 5) DATENQUALITÄT — DQ-Panel                                                   -->
            <!-- ============================================================================= -->
            @if (activeNavItem === 'dataQuality') {

                <!-- Was zeigt diese Seite? -->
                <p-accordion class="block mb-3">
                    <p-accordion-panel value="0">
                        <p-accordion-header>
                            <span class="flex align-items-center gap-2">
                                <i class="fa-regular fa-circle-question text-primary"></i>
                                <span class="font-medium">Was zeigt diese Seite?</span>
                            </span>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-700 p-2">
                                Abdeckung, unvollständige Berechnungsbasis und nicht zugeordnete
                                Positionen. Die Datenqualität ist jahres- und gesellschaftsbezogen —
                                die Monatsauswahl wirkt auf das Jahr.
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>

                <!-- ===================== DATENQUALITÄT (DQ-PANEL) =====================
                     Hans-Jürgen MAJOR: the existing mvs-cn-data-quality-panel honours the global Periode +
                     Gesellschaft filter — we pass the same resolved facets the inbox + cockpit use, so the DQ
                     counters are scoped instead of showing global numbers. The panel self-fetches the (now
                     scoped) counters and recomputes whenever the filter changes. -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-3">
                        <i class="fa-regular fa-shield-check text-gray-600"></i>
                        <span class="text-lg font-medium">Datenqualität</span>
                        <i class="fa-regular fa-circle-info text-gray-500 cursor-help"
                           pTooltip="Datenqualität ist jahres- und gesellschaftsbezogen — die Monatsauswahl wirkt auf das Jahr."
                           tooltipPosition="top"></i>
                    </div>
                    <mvs-cn-data-quality-panel
                        [period]="resolvePeriod()"
                        [partnerId]="resolvePartnerId()">
                    </mvs-cn-data-quality-panel>
                </div>
            }

            <!-- ============================================================================= -->
            <!-- 7) SOLL VS IST (VERLAUF) — monthly Soll-vs-Ist Gesamt line chart             -->
            <!-- ============================================================================= -->
            @if (activeNavItem === 'sollIstTrend') {

                <!-- Was zeigt diese Seite? -->
                <p-accordion class="block mb-3">
                    <p-accordion-panel value="0">
                        <p-accordion-header>
                            <span class="flex align-items-center gap-2">
                                <i class="fa-regular fa-circle-question text-primary"></i>
                                <span class="font-medium">Was zeigt diese Seite?</span>
                            </span>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-700 p-2">
                                Monatlicher Verlauf der BERECHNETEN Provision (Soll) gegenüber der
                                empfangenen/quittierten (Ist). Beide Linien zeigen den Gesamtwert je
                                Monat im gewählten Zeitraum.
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>

                <!-- ===================== SOLL VS IST (VERLAUF) =====================
                     A monthly LINE chart comparing Soll Gesamt (GET /cn/calculation/trend, range-aware)
                     against Ist Gesamt (GET /cn/activity/totals/trend, months-window). Both series are
                     aligned by YYYY-MM month label; a month present in only one series reads 0 on the
                     other line. NO calculation in the FE on the EUR figures. -->
                <div class="surface-0 border-1 surface-border border-round-lg p-3 mb-3">
                    <div class="flex align-items-center gap-2 mb-2">
                        <i class="fa-regular fa-chart-line text-gray-600"></i>
                        <span class="text-lg font-medium">Soll vs Ist (Verlauf)</span>
                        <i class="fa-regular fa-circle-info text-gray-500 cursor-help"
                           pTooltip="Soll = berechnete Provisionserwartung je Monat (GET /cn/calculation/trend, zeitraumgenau). Ist = empfangene/quittierte Provision je Monat. Gleicher Zeitraum- und Gesellschaft-Filter wie das übrige Dashboard."
                           tooltipPosition="top"></i>
                    </div>

                    @switch (sollIstTrendState) {
                        @case ('loading') {
                            <div class="flex align-items-center gap-2 text-gray-600 p-3">
                                <i class="fa-regular fa-spinner fa-spin"></i>
                                <span>Soll-vs-Ist-Verlauf wird geladen …</span>
                            </div>
                        }
                        @case ('error') {
                            <p-message severity="error" class="block" [text]="sollIstTrendError"></p-message>
                        }
                        @case ('empty') {
                            <div class="text-center text-gray-600 p-4">
                                Kein Verlauf für den gewählten Zeitraum.
                            </div>
                        }
                        @case ('ready') {
                            <div style="height: 22rem">
                                <p-chart type="line" [data]="sollIstChartData" [options]="sollIstChartOptions"
                                         height="100%"></p-chart>
                            </div>
                            <!-- Known limitation (v1): the Ist series uses a Monatsfenster ending at the
                                 current month and has no Von/Bis-Facette; bei einem komplett in der
                                 Vergangenheit liegenden Zeitraum kann die Ausrichtung daher abweichen. -->
                            <div class="flex align-items-start gap-2 text-xs text-gray-500 mt-3">
                                <i class="fa-regular fa-circle-info mt-1"></i>
                                <span>
                                    Hinweis: Die Ist-Reihe verwendet ein Monatsfenster, das am aktuellen
                                    Monat endet. Liegt der gewählte Zeitraum vollständig in der
                                    Vergangenheit, kann die Ausrichtung der beiden Linien abweichen
                                    (akzeptiert für v1).
                                </span>
                            </div>
                        }
                    }
                </div>
            }

        </div>
    </div>
}
